import { useCallback, useMemo, useRef, useState } from 'react';

import { Tabs, Tooltip } from 'antd';
import { useMedia } from 'react-media';

import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';

import moment from 'moment';

// Third-party
import { Button, Col, DatePicker, Row, Space, Typography } from 'antd';
import { AgGridReact } from 'ag-grid-react';

// Shared components
import { FormAddButton, FormDeleteButton } from 'common/components';

// Wizard components
import WizardContentBox from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardContentBox';
import { WizardDescription } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardDescription';
import { WizardHierarchyAction } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardHierarchyAction';

// Wizard hooks / context
import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';
import { useOnlineCoreGridColumns } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useOnlineCoreGridColumns';
import useBeverageGridColumns from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useBeverageGridColumns';
import {
  useMultipackGridColumns,
  MultipackDetailCellRenderer,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useMultipackGridColumns';
import {
  useShipperGridColumns,
  ShipperDetailCellRenderer,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useShipperGridColumns';

// Config
import { GLOBAL_MEDIA_QUERIES } from 'static/MediaQuery';

// Enterprise — registers MasterDetail and all other enterprise modules
import 'common/components/ag-grid/registerAgGridModules';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/styles/SubmissionGrid.less';

// A rowData update re-styles only cells whose value changed, so a fixed cell would keep its error style
const refreshCellsWithNewRowData = ({ api }) =>
  api.refreshCells({ force: true });

const DEFAULT_COL_DEF = {
  sortable: false,
  resizable: true,
  filter: false,
};

const getRowIdFromUniqueId = (params) => params.data?.uniqueId;

// ─── Shared tab logic ─────────────────────────────────────────────────────────

const useWizardTabs = (answers) => {
  const {
    beer,
    wine,
    spirits,
    alcoholicBeverages,
    displayShippers,
    multipacks,
  } = answers;

  const activeBeverageTabs = useMemo(
    () =>
      [
        beer && { key: 'beer', label: 'Beer', type: 'beer' },
        wine && { key: 'wine', label: 'Wine', type: 'wine' },
        spirits && { key: 'spirits', label: 'Spirits', type: 'spirits' },
      ].filter(Boolean),
    [beer, wine, spirits]
  );

  const hasBeverageTabs = alcoholicBeverages && activeBeverageTabs.length > 0;
  const hasShipperTab = Boolean(displayShippers);
  const hasMultipackTab = Boolean(multipacks);
  const hasTabs = hasBeverageTabs || hasShipperTab || hasMultipackTab;

  return {
    activeBeverageTabs,
    hasBeverageTabs,
    hasShipperTab,
    hasMultipackTab,
    hasTabs,
  };
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

export const OnlineGridSection = (props) => {
  const { wizardGrid, wizardGridAction, onAddNewProduct, onFindProducts, disabled } = props;

  const { actions, state } = useWizard();

  const { arrivalDate } = state;
  const { hasTabs } = useWizardTabs(state.answers);

  return (
    <WizardContentBox className='wizard-submission-grid'>
      <div className='wizard-submission-grid__intro'>
        <Typography.Title level={5}>Add products to the Product Submission Grid</Typography.Title>
        <WizardDescription>
          Add new products or search for existing products. When you’re ready,
          review the grid and click Submit to complete your product submission.
        </WizardDescription>
      </div>
      <Row gutter={[24, 20]} className='wizard-submission-grid__guidance'>
        <Col xs={24} lg={12}>
          <Typography.Title level={5}>1. Add or find products</Typography.Title>
          <WizardDescription>
            Choose an option below to add products to the grid.
          </WizardDescription>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12}>
              <SubmissionChoice
                icon={<PlusOutlined />}
                title='Add Product'
                description='Use when your product is not on the SPINS IX-ONE platform.'
                disabled={disabled}
                onClick={onAddNewProduct}
              />
            </Col>
            <Col xs={24} sm={12}>
              <SubmissionChoice
                icon={<SearchOutlined />}
                title='Find Products'
                description='Use to find existing products on the SPINS IX-ONE platform.'
                disabled={disabled}
                onClick={onFindProducts}
              />
            </Col>
          </Row>
          <Space
            align='start'
            className='wizard-submission-grid__note wizard-submission-grid__note--remove'
          >
            <DeleteOutlined />
            <Typography.Text>
              Use the Remove Product button above the grid if you need to remove
              an item from this submission. This does not remove the item from
              our platform.
            </Typography.Text>
          </Space>
        </Col>
        <Col xs={24} lg={12} className='wizard-submission-grid__arrival'>
          <Typography.Title level={5}>
            2. Expected studio arrival date{' '}
            <Typography.Text type='secondary'>(optional)</Typography.Text>
          </Typography.Title>
          <WizardDescription>
            Let us know the estimated date your products will arrive at the IX-ONE studio
            for capture.
          </WizardDescription>
          <DatePicker
            className='wizard-submission-grid__date'
            value={arrivalDate || null}
            onChange={actions.setDate}
            disabledDate={(current) => current && current < moment().startOf('day')}
          />
          <WizardDescription>
            Studio is closed on weekends and major holidays. This date is an
            estimate and may be updated if needed.
          </WizardDescription>
          <Space align='start' className='wizard-submission-grid__note'>
            <InfoCircleOutlined />
            <Typography.Text>
              IX-ONE captures data from the case level down to the unit to support
              data collection requirements across many retailers and distributors.
            </Typography.Text>
          </Space>
        </Col>
      </Row>
      {hasTabs && (
        <WizardDescription>
          Complete product data on the Core Data tab before adding details in the
          additional tabs, which are based on your answers.
        </WizardDescription>
      )}
      <div className='make-product-active__ixone-grid'>
        {wizardGridAction}
        {wizardGrid}
        <Typography.Paragraph className='wizard-submission-grid__hint'>
          To save your progress and continue later, click Save.
        </Typography.Paragraph>
      </div>
    </WizardContentBox>
  );
};

const SubmissionChoice = ({ icon, title, description, disabled, onClick }) => (
  <Button
    block
    icon={icon}
    className='wizard-submission-grid__choice'
    disabled={disabled}
    onClick={onClick}
  >
    <span className='wizard-submission-grid__choice-text'>
      <Typography.Text strong>{title}</Typography.Text>
      <Typography.Text>{description}</Typography.Text>
    </span>
    <RightOutlined />
  </Button>
);

export const WizardGridAction = ({
  onAddNewProduct,
  onFindProducts,
  selectedRow,
  onDeleteProduct,
  disabled = false,
}) => {
  return (
    <Row
      justify='space-between'
      align='middle'
      gutter={[12, 8]}
      className='wizard-submission-grid__actions'
    >
      <Col>
        <Tooltip title='Use to find existing products on the SPINS IX-ONE platform.'>
          <FormAddButton
            text='Find Products'
            type='default'
            className='wizard-submission-grid__find-button'
            onClick={onFindProducts}
            icon={<SearchOutlined />}
            disabled={disabled}
          />
        </Tooltip>
      </Col>
      <Col>
        <Space wrap>
          <Tooltip title='Use when your product is not on the SPINS IX-ONE platform.'>
            <FormAddButton
              text='Add Product'
              onClick={onAddNewProduct}
              disabled={disabled}
            />
          </Tooltip>
          {/* Lisa wanna edit inline grid */}
          {/* <FormEditButton
            text='Edit Product'
            onClick={onEditProduct}
            disabled={!selectedRow}
          /> */}
          <FormDeleteButton
            text='Remove Product'
            onClick={onDeleteProduct}
            disabled={disabled || !selectedRow}
          />
        </Space>
      </Col>
    </Row>
  );
};

// ─── Responsive grid height ───────────────────────────────────────────────────

const useGridHeight = () => {
  const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });
  if (matches.sm || matches.md) return 220;
  return 320;
};

// ─── Main product AG Grid ─────────────────────────────────────────────────────

export const WizardGrid = ({
  onSelectionChange,
  onCellValueChanged,
  onRecaptureRow,
  onClearSelectedRow,
  memberId,
  gridRef,
  unitUpcOptions = [],
}) => {
  const { state } = useWizard();
  const gridHeight = useGridHeight();

  const [hierarchyTarget, setHierarchyTarget] = useState(null);

  const handleOpenHierarchy = useCallback((data) => {
    setHierarchyTarget(data);
  }, []);

  const handleCloseHierarchy = useCallback(() => {
    setHierarchyTarget(null);

    const targetUniqueId = hierarchyTarget?.uniqueId;

    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api || !targetUniqueId) return;
      const node = api.getRowNode(targetUniqueId);
      if (node) node.setSelected(true);
    }, 0);
  }, [gridRef, hierarchyTarget?.uniqueId]);

  const { columnDefs } = useOnlineCoreGridColumns(
    memberId,
    onRecaptureRow,
    state?.answers,
    handleOpenHierarchy,
    state?.products
  );

  const mappingProducts = useMemo(() => {
    return state?.products;
  }, [state?.products]);

  const {
    activeBeverageTabs,
    hasBeverageTabs,
    hasShipperTab,
    hasMultipackTab,
    hasTabs,
  } = useWizardTabs(state.answers);

  const coreDataGrid = (
    <div
      className='ag-theme-alpine ag-theme-alpine--with-scroller'
      style={{ width: '100%', height: gridHeight }}
    >
      <AgGridReact
        theme="legacy"
        columnDefs={columnDefs}
        rowData={mappingProducts}
        headerHeight={32}
        rowHeight={34}
        rowSelection={'single'}
        defaultColDef={DEFAULT_COL_DEF}
        stopEditingWhenCellsLoseFocus
        singleClickEdit
        onCellValueChanged={onCellValueChanged}
        className='wizard-content-box__data-grid'
        overlayNoRowsTemplate='No Rows to Show'
        onSelectionChanged={onSelectionChange}
        ref={gridRef}
        getRowId={getRowIdFromUniqueId}
        onRowDataUpdated={refreshCellsWithNewRowData}
        context={{ onCellValueChanged }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {hasTabs ? (
        <Tabs
          size='middle'
          defaultActiveKey='coreData'
          destroyInactiveTabPane
          onChange={() => {
            onClearSelectedRow();
          }}
        >
          <Tabs.TabPane
            tab={
              <Typography.Text style={{ fontSize: 14 }}>
                Core Data
              </Typography.Text>
            }
            key='coreData'
          >
            {coreDataGrid}
          </Tabs.TabPane>

          {hasBeverageTabs &&
            activeBeverageTabs.map(({ key, label, type }) => (
              <Tabs.TabPane
                tab={
                  <Typography.Text style={{ fontSize: 14 }}>
                    {label}
                  </Typography.Text>
                }
                key={key}
              >
                <BeverageGrid
                  beverageType={type}
                  products={mappingProducts}
                  unitUpcOptions={unitUpcOptions}
                  onCellValueChanged={onCellValueChanged}
                  gridHeight={gridHeight}
                />
              </Tabs.TabPane>
            ))}

          {hasShipperTab && (
            <Tabs.TabPane
              tab={
                <Typography.Text style={{ fontSize: 14 }}>
                  Shipper Content
                </Typography.Text>
              }
              key='shipper'
            >
              <ShipperGrid
                products={mappingProducts}
                unitUpcOptions={unitUpcOptions}
                onCellValueChanged={onCellValueChanged}
                gridHeight={gridHeight}
              />
            </Tabs.TabPane>
          )}

          {hasMultipackTab && (
            <Tabs.TabPane
              tab={
                <Typography.Text style={{ fontSize: 14 }}>
                  Multipack Content
                </Typography.Text>
              }
              key='multipack'
            >
              <MultipackGrid
                products={mappingProducts}
                unitUpcOptions={unitUpcOptions}
                onCellValueChanged={onCellValueChanged}
                gridHeight={gridHeight}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      ) : (
        coreDataGrid
      )}

      {hierarchyTarget && (
        <WizardHierarchyAction
          productId={
            hierarchyTarget.productUnitId ||
            hierarchyTarget.productCaseId ||
            hierarchyTarget.productInnerPackId ||
            hierarchyTarget.productId
          }
          productName={hierarchyTarget.productName}
          uniqueId={hierarchyTarget.uniqueId}
          visible
          onClose={handleCloseHierarchy}
        />
      )}
    </div>
  );
};

// ─── Beverage tab grid ────────────────────────────────────────────────────────

const BeverageGrid = ({
  beverageType,
  products,
  unitUpcOptions,
  onCellValueChanged,
  gridHeight = 320,
}) => {
  const gridRef = useRef(null);
  const { columnDefs } = useBeverageGridColumns(
    beverageType,
    unitUpcOptions,
    products
  );

  return (
    <div
      className='ag-theme-alpine ag-theme-alpine--with-scroller'
      style={{ width: '100%', height: gridHeight }}
    >
      <AgGridReact
        theme="legacy"
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={products}
        headerHeight={32}
        rowHeight={34}
        rowSelection='single'
        defaultColDef={DEFAULT_COL_DEF}
        stopEditingWhenCellsLoseFocus
        singleClickEdit
        overlayNoRowsTemplate='No Rows to Show'
        onCellValueChanged={onCellValueChanged}
        getRowId={getRowIdFromUniqueId}
        onRowDataUpdated={refreshCellsWithNewRowData}
      />
    </div>
  );
};

// ─── Shipper tab grid — master/detail ────────────────────────────────────────

const ShipperGrid = ({
  products,
  unitUpcOptions,
  onCellValueChanged,
  gridHeight = 320,
}) => {
  const gridRef = useRef(null);

  const shipperRowData = useMemo(
    () =>
      products
        .filter((p) => p.displayShipper)
        .map(({ displayShipperItems, ...rest }) => rest),
    [products]
  );
  const { columnDefs } = useShipperGridColumns(unitUpcOptions, products);

  return (
    <div
      className='ag-theme-alpine ag-theme-alpine--with-scroller'
      style={{ width: '100%', height: gridHeight }}
    >
      <AgGridReact
        theme="legacy"
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={shipperRowData}
        headerHeight={32}
        rowHeight={34}
        rowSelection='single'
        defaultColDef={DEFAULT_COL_DEF}
        stopEditingWhenCellsLoseFocus
        singleClickEdit
        overlayNoRowsTemplate='No Rows to Show'
        onCellValueChanged={onCellValueChanged}
        getRowId={getRowIdFromUniqueId}
        onRowDataUpdated={refreshCellsWithNewRowData}
        alwaysShowVerticalScroll
        masterDetail
        isRowMaster={() => true}
        detailRowAutoHeight
        detailCellRenderer={ShipperDetailCellRenderer}
        keepDetailRows
      />
    </div>
  );
};

export default OnlineGridSection;

// ─── Multipack tab grid — master/detail ──────────────────────────────────────

const MultipackGrid = ({
  products,
  unitUpcOptions,
  onCellValueChanged,
  gridHeight = 320,
}) => {
  const gridRef = useRef(null);

  const multipackRowData = useMemo(
    () =>
      products
        .filter((p) => p.multipack)
        .map(({ multipackItems, ...rest }) => rest),
    [products]
  );

  const { columnDefs } = useMultipackGridColumns(unitUpcOptions, products);

  return (
    <div
      className='ag-theme-alpine ag-theme-alpine--with-scroller'
      style={{ width: '100%', height: gridHeight }}
    >
      <AgGridReact
        theme="legacy"
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={multipackRowData}
        headerHeight={32}
        rowHeight={34}
        rowSelection='single'
        defaultColDef={DEFAULT_COL_DEF}
        stopEditingWhenCellsLoseFocus
        singleClickEdit
        overlayNoRowsTemplate='No Rows to Show'
        onCellValueChanged={onCellValueChanged}
        getRowId={getRowIdFromUniqueId}
        onRowDataUpdated={refreshCellsWithNewRowData}
        alwaysShowVerticalScroll
        masterDetail
        isRowMaster={() => true}
        detailRowAutoHeight
        detailCellRenderer={MultipackDetailCellRenderer}
        keepDetailRows
      />
    </div>
  );
};
